import { Bot, Check, ChevronDown, FolderKanban, Route, Search, Sparkles } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Toggle } from "@/components/shared";
import type { Agent } from "@/types";

export interface AgentPickerProps {
  /** Agents attached to the current project (or all accessible agents when not in a project). */
  agents: Agent[];
  /** General-purpose agents not attached to the project. Empty when not in a project. */
  nonProjectAgents?: Agent[];
  /** Whether the chat is scoped to a project (controls the toggle visibility). */
  inProject?: boolean;
  selectedAgentUuid: string | null;
  onSelect: (uuid: string | null) => void;
  disabled?: boolean;
  /** Offers an "Auto" row: the best agent is picked when the first message is sent (MHS-627). */
  auto?: { selected: boolean; onSelect: () => void };
  /** Offers "No Agent" (plain chat); off when switching an open chat to another agent (MHS-628). */
  noAgentOption?: boolean;
  /** Trigger label when no agent is selected. */
  emptyLabel?: string;
  /** Which edge of the trigger the dropdown lines up with. */
  align?: "left" | "right";
}

/**
 * Searchable agent picker for the chat header.
 *
 * - Search box filters by agent name/description.
 * - When inside a project, a toggle reveals general-purpose (non-project)
 *   agents grouped separately, so users can pull in e.g. a PPTX builder.
 * - Always offers a "No Agent" (plain chat) option.
 * - Optionally offers "Auto" (best agent picked on send).
 */
export function AgentPicker({
  agents,
  nonProjectAgents = [],
  inProject = false,
  selectedAgentUuid,
  onSelect,
  disabled = false,
  auto,
  noAgentOption = true,
  emptyLabel = "No Agent",
  align = "right",
}: Readonly<AgentPickerProps>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [showAllAgents, setShowAllAgents] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Focus the search box when opening
  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  // Resolve the currently-selected agent against the FULL known set (project +
  // non-project), independent of the "show all" toggle. Otherwise the trigger
  // label would show "No Agent" for a selected non-project agent whenever the
  // toggle is off (it resets to false on remount).
  const selectedAgent = useMemo(() => {
    if (!selectedAgentUuid) return null;
    return (
      agents.find((a) => a.uuid === selectedAgentUuid) ??
      nonProjectAgents.find((a) => a.uuid === selectedAgentUuid) ??
      null
    );
  }, [agents, nonProjectAgents, selectedAgentUuid]);

  // If the selection is a non-project agent, force the "show all" section open
  // so it's visible and deselectable in the list.
  const selectionIsNonProject =
    !!selectedAgentUuid && !agents.some((a) => a.uuid === selectedAgentUuid) && !!selectedAgent;
  const effectiveShowAll = showAllAgents || (inProject && selectionIsNonProject);

  const filter = (list: Agent[]) => {
    const q = query.trim().toLowerCase();
    const matches = q
      ? list.filter((a) => a.name.toLowerCase().includes(q) || (a.description ?? "").toLowerCase().includes(q))
      : list;
    return [...matches].sort((a, b) => a.name.localeCompare(b.name));
  };

  const projectMatches = filter(agents);
  const nonProjectMatches =
    inProject && effectiveShowAll ? filter(nonProjectAgents.filter((a) => !agents.some((p) => p.uuid === a.uuid))) : [];

  const handleSelect = (uuid: string | null) => {
    onSelect(uuid);
    setOpen(false);
    setQuery("");
  };

  let triggerLabel = selectedAgent ? selectedAgent.name : emptyLabel;
  if (auto?.selected) triggerLabel = "Auto";

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 bg-surface-secondary text-content-primary pl-3 pr-2 py-2 rounded-lg border border-border-secondary focus:outline-hidden focus:ring-2 focus:ring-brand-primary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed max-w-[16rem]"
      >
        <Bot className="w-4 h-4 text-content-tertiary shrink-0" />
        <span className="truncate text-sm">{triggerLabel}</span>
        <ChevronDown className="w-4 h-4 text-content-tertiary shrink-0" />
      </button>

      {open && (
        <div
          className={`absolute z-50 mt-2 w-80 max-h-[28rem] overflow-hidden flex flex-col bg-surface-primary border border-border-primary rounded-xl shadow-xl ${align === "left" ? "left-0" : "right-0"}`}
        >
          {/* Search */}
          <div className="p-2 border-b border-border-primary">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary pointer-events-none" />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search agents…"
                className="w-full bg-surface-secondary text-content-primary text-sm pl-9 pr-3 py-2 rounded-lg border border-border-secondary focus:outline-hidden focus:ring-2 focus:ring-brand-primary"
              />
            </div>
          </div>

          {/* Project toggle */}
          {inProject && nonProjectAgents.length > 0 && (
            <div className="px-3 py-2 border-b border-border-primary">
              <Toggle
                checked={effectiveShowAll}
                onChange={setShowAllAgents}
                label="Show all agents"
                labelPosition="left"
                disabled={selectionIsNonProject}
              />
            </div>
          )}

          <div className="overflow-y-auto py-1">
            {auto && (
              <PickerRow
                icon={<Route className="w-4 h-4 text-brand-primary" />}
                label="Auto"
                sublabel="Picks the best agent when you send"
                selected={auto.selected}
                onClick={() => {
                  auto.onSelect();
                  setOpen(false);
                  setQuery("");
                }}
              />
            )}

            {/* No Agent option */}
            {noAgentOption && (
              <PickerRow
                icon={<Sparkles className="w-4 h-4 text-content-tertiary" />}
                label="No Agent"
                sublabel="Plain chat, no agent"
                selected={!selectedAgentUuid && !auto?.selected}
                onClick={() => handleSelect(null)}
              />
            )}

            {/* Project agents (or all accessible agents when not in a project) */}
            {projectMatches.length > 0 && (
              <>
                {inProject && (
                  <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-content-quaternary flex items-center gap-1">
                    <FolderKanban className="w-3 h-3" />
                    Project agents
                  </div>
                )}
                {projectMatches.map((agent) => (
                  <PickerRow
                    key={agent.uuid}
                    icon={<Bot className="w-4 h-4" style={{ color: agent.color || undefined }} />}
                    label={agent.name}
                    sublabel={agent.description || undefined}
                    selected={agent.uuid === selectedAgentUuid}
                    onClick={() => handleSelect(agent.uuid)}
                  />
                ))}
              </>
            )}

            {/* Non-project agents (only when toggled on inside a project) */}
            {nonProjectMatches.length > 0 && (
              <>
                <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-content-quaternary">
                  All agents
                </div>
                {nonProjectMatches.map((agent) => (
                  <PickerRow
                    key={agent.uuid}
                    icon={<Bot className="w-4 h-4" style={{ color: agent.color || undefined }} />}
                    label={agent.name}
                    sublabel={agent.description || undefined}
                    selected={agent.uuid === selectedAgentUuid}
                    onClick={() => handleSelect(agent.uuid)}
                  />
                ))}
              </>
            )}

            {projectMatches.length === 0 && nonProjectMatches.length === 0 && query.trim() && (
              <div className="px-3 py-4 text-sm text-content-tertiary text-center">No agents match "{query}"</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PickerRow({
  icon,
  label,
  sublabel,
  selected,
  onClick,
}: Readonly<{
  icon: React.ReactNode;
  label: string;
  sublabel?: string;
  selected: boolean;
  onClick: () => void;
}>) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-interactive-hover transition ${
        selected ? "bg-brand-primary/10" : ""
      }`}
    >
      <span className="shrink-0">{icon}</span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm text-content-primary truncate">{label}</span>
        {sublabel && <span className="block text-xs text-content-tertiary truncate">{sublabel}</span>}
      </span>
      {selected && <Check className="w-4 h-4 text-brand-primary shrink-0" />}
    </button>
  );
}
